import { useEffect, useState, type CSSProperties } from 'react'
import { HomeCandidateDiscovery } from '@/components/home/HomeCandidateDiscovery'
import { HomeHero } from '@/components/home/HomeHero'
import { HomeNewsSection } from '@/components/home/HomeNewsSection'
import { HomeVideoPanel } from '@/components/home/HomeVideoPanel'
import { usePageLoading } from '@/components/layout/PageLoadingProvider'
import { useTerritoryLocation } from '@/components/location/TerritoryLocationProvider'
import { publicAsset } from '@/components/navigation/navigationAssets'
import { VotePriority } from '@/components/priority/VotePriority'
import { CitizenReportForm } from '@/components/reporting/CitizenReportForm'
import { CneVotingLookup } from '@/components/voting/CneVotingLookup'
import { HowToVoteRequirements } from '@/components/voting/HowToVoteRequirements'
import { VotingPlaceSection } from '@/components/voting/VotingPlaceSection'
import { getElectionNews } from '@/lib/data/news'
import type { NormalizedNewsItem } from '@/types/news'

export function HomePage() {
  const [news, setNews] = useState<NormalizedNewsItem[]>([])
  const [newsStatus, setNewsStatus] = useState<'loading' | 'ready' | 'error'>('loading')
  usePageLoading(newsStatus === 'loading')
  const { detectedTerritory, locationStatusText } = useTerritoryLocation()
  const textureStyle = {
    '--home-paper-texture': `url("${publicAsset('textures/textura-papel.webp')}")`,
    '--home-report-image-desktop': `url("${publicAsset('images/figma/home/citizen-report-desktop.png')}")`,
    '--home-report-image-mobile': `url("${publicAsset('images/figma/home/citizen-report-mobile.png')}")`,
  } as CSSProperties

  useEffect(() => {
    let isMounted = true

    getElectionNews()
      .then((items) => {
        if (!isMounted) return
        setNews(items)
        setNewsStatus('ready')
      })
      .catch(() => {
        if (!isMounted) return
        setNewsStatus('error')
      })

    return () => {
      isMounted = false
    }
  }, [])

  return (
    <div className="home-page home-page--figma" style={textureStyle}>
      <HomeHero />
      <HomeCandidateDiscovery />

      <div className="home-redesign-grid">
        <div className="home-redesign-grid__latest">
          <HomeNewsSection news={news.slice(4, 7)} status={newsStatus} variant="sidebar" />
        </div>

        <div className="home-redesign-grid__priority">
          <VotePriority showCityOpinion={false} />
        </div>

        <div className="home-redesign-grid__news">
          <HomeNewsSection news={news} status={newsStatus} variant="home" />
        </div>

        <div className="home-redesign-grid__cne-lookup">
          <CneVotingLookup />
        </div>

        <div className="home-redesign-grid__voting">
          <VotingPlaceSection
            detectedTerritory={detectedTerritory}
            locationStatusText={locationStatusText}
          />
        </div>

        <div className="home-redesign-grid__guide">
          <HowToVoteRequirements variant="home" showAd={false} />
        </div>

        <section className="home-redesign-grid__interviews" aria-label="Entrevistas a candidatos">
          <HomeVideoPanel variant="home" />
        </section>

        <section className="home-redesign-grid__reporting" aria-label="Los problemas de tu ciudad">
          <CitizenReportForm
            title="Los problemas de tu ciudad"
            compact
            territory={detectedTerritory}
          />
        </section>
      </div>
    </div>
  )
}
